import {
  type HsiDriftReason,
  type HsiRecomputeProgress,
  hsiStatusLine,
} from '../models/hsiRecompute'
import { type HistoryProgress, historyLabel } from '../models/historyDownload'
import { useT } from '../i18n'
import { type CardProgress, cardProgressLabel, cardProgressPercent } from './cardRefresh'
import type { LoadState } from './useLoadData'

interface LoadDataButtonProps {
  state: LoadState
  progress: number
  error: string | null
  onClick: () => void
  /** Прогресс точечного добора позиций судов; null — показывать нечего. */
  cardProgress?: CardProgress | null
  /** Прогресс расчёта HSI, запущенного цепочкой слоя «Траектории по HSI»;
   *  null — расчёт не идёт. Своя полоска, как у судов: это вторая, более долгая
   *  работа, и блокировать ею кнопку нельзя. */
  hsiProgress?: HsiRecomputeProgress | null
  /** Причина из ПОСЛЕДНЕГО ответа ручки усиков — про кадр на экране, а не про
   *  кадр, где заказывали расчёт (см. hsiStatusLine). null — слой выключен. */
  hsiReason?: HsiDriftReason | null
  /** Докачка истории течений под усики: её заказывает этот же клик, когда
   *  включён слой «Траектории HSI» (см. shouldFillHistory). */
  historyProgress?: HistoryProgress | null
}

export function LoadDataButton({
  state,
  progress,
  error,
  onClick,
  cardProgress = null,
  hsiProgress = null,
  hsiReason = null,
  historyProgress = null,
}: LoadDataButtonProps) {
  const busy = state === 'preparing' || state === 'queued' || state === 'downloading'
  const t = useT()
  let label = t('map.load.button')
  if (state === 'preparing') label = t('map.load.preparing')
  // Запрос принят, но воркер его ещё не взял. Причину ожидания не называем —
  // человеку важно, что он в очереди, а не чем занят сервер (см. loadState.ts).
  else if (state === 'queued') label = t('map.load.queued')
  else if (state === 'downloading') label = t('map.load.downloading', { percent: progress })
  else if (state === 'ready') label = t('map.load.refresh')
  else if (state === 'failed') label = t('map.load.retry')
  const hsiLine = hsiStatusLine({ progress: hsiProgress, reason: hsiReason, busy })

  return (
    // pointer-events-auto — свой: кнопку кладут в оверлей карты с pointer-events-none,
    // и без этого клик проваливается на карту (вместо загрузки срабатывает драг).
    <div className="pointer-events-auto flex flex-col items-center gap-1.5">
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className={[
          'flex items-center gap-2.5 rounded-xl px-6 py-3 text-sm font-semibold tracking-[0.01em] transition-colors',
          busy
            ? 'cursor-wait bg-trench text-mist shadow-console'
            : 'bg-gradient-to-b from-sonar to-sonar-dim text-abyss shadow-sonar hover:from-sonar hover:to-sonar',
        ].join(' ')}
      >
        <span
          aria-hidden
          className={['h-2 w-2 rounded-full', busy ? 'bg-sonar' : 'bg-abyss'].join(' ')}
        />
        {label}
      </button>
      {busy && (
        <div className="h-1 w-40 overflow-hidden rounded-full bg-abyss/50">
          <div
            className="h-full rounded-full bg-sonar transition-[width] duration-300"
            style={{ width: `${state === 'downloading' ? progress : 15}%` }}
          />
        </div>
      )}
      {/* Суда — отдельная, более долгая работа: карточки MarineTraffic идут
          минуты, тогда как океанография уже готова. Поэтому своя полоска, а не
          захват кнопки: блокировать «Обновить» ради побочной работы нельзя. */}
      {cardProgress && (
        <div className="flex flex-col items-center gap-1">
          {!cardProgress.done && (
            <div className="h-1 w-40 overflow-hidden rounded-full bg-abyss/50">
              <div
                className="h-full rounded-full bg-mist transition-[width] duration-300"
                // Пока объём не посчитан, полоска стоит на четверти: это признак
                // «работа идёт», а не измеренная доля.
                style={{
                  width: cardProgress.total
                    ? `${cardProgressPercent(cardProgress)}%`
                    : '25%',
                }}
              />
            </div>
          )}
          <span className="text-[11px] tracking-[0.02em] text-mist">
            {cardProgressLabel(cardProgress)}
          </span>
        </div>
      )}
      {/* Расчёт HSI — вторая ступень цепочки «Траекторий по HSI»: течения уже
          готовы, а поле вида ещё считается. Полоска своя, чтобы было видно, что
          работа продолжается, а не зависла кнопка. */}
      {hsiLine && (
        <div className="flex flex-col items-center gap-1">
          {hsiLine.percent != null && (
            <div className="h-1 w-40 overflow-hidden rounded-full bg-abyss/50">
              <div
                className="h-full rounded-full bg-sonar-dim transition-[width] duration-300"
                style={{ width: `${Math.max(3, hsiLine.percent)}%` }}
              />
            </div>
          )}
          <span
            className={[
              'max-w-xs text-center text-[11px] tracking-[0.02em]',
              hsiLine.tone === 'error'
                ? 'text-coral'
                : hsiLine.tone === 'warn'
                  ? 'text-buoy'
                  : 'text-mist',
            ].join(' ')}
          >
            {t(hsiLine.key, hsiLine.params)}
          </span>
        </div>
      )}
      {/* Докачка истории течений: клик заказывает недостающие сутки под усики,
          и работа шла втёмную — полоска показывает её до конца. */}
      {historyProgress && (
        <div className="flex flex-col items-center gap-1">
          {!historyProgress.done && (
            <div className="h-1 w-40 overflow-hidden rounded-full bg-abyss/50">
              <div
                className="h-full rounded-full bg-sonar-dim transition-[width] duration-300"
                style={{ width: `${Math.max(3, historyProgress.percent)}%` }}
              />
            </div>
          )}
          <span
            className={[
              'text-[11px] tracking-[0.02em]',
              historyProgress.failed ? 'text-buoy' : 'text-mist',
            ].join(' ')}
          >
            {historyLabel(historyProgress)}
          </span>
        </div>
      )}
      {state === 'failed' && error && (
        <span className="max-w-xs rounded-md border border-coral/40 bg-coral/10 px-2.5 py-1 text-xs text-coral">
          {error}
        </span>
      )}
    </div>
  )
}
